import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import "./index.css";

function Dashboard() {
  const [hotels, setHotels] = useState([{ url: "", price: "" }]);
  const navigate = useNavigate();
  const location = useLocation();

  const [offerDetails, setOfferDetails] = useState({
    customerName: "",
    customerEmail: "",
    agentName: "",
    agentNumber: "",
    showName: "",
    companyName: "",
    cityState: "",
    dateFrom: "",
    dateTo: ""
  });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Pre-fill form if editing an offer
  useEffect(() => {
    if (location.state && location.state.editOffer) {
      const data = location.state.editOffer;
      
      // Try to parse dates back to From/To inputs if possible
      let dFrom = "";
      let dTo = "";
      
      // Case 1: Object {from, to}
      if (typeof data.dates === "object" && data.dates !== null) {
          dFrom = data.dates.from || "";
          dTo = data.dates.to || "";
      } 
      // Case 2: String "From X To Y"
      else if (typeof data.dates === "string" && data.dates.includes("From")) {
          const parts = data.dates.match(/From (.*) To (.*)/);
          if (parts && parts.length === 3) {
            dFrom = parts[1];
            dTo = parts[2];
          }
      }

      setOfferDetails({
        customerName: data.customerName || "",
        customerEmail: data.customerEmail || "",
        agentName: data.agentName || "",
        agentNumber: data.agentNumber || "",
        showName: data.showName || "",
        companyName: data.companyName || "",
        cityState: data.cityState || "",
        dateFrom: dFrom,
        dateTo: dTo
      });

      // Pre-fill hotels if available
      if (data.hotels && Array.isArray(data.hotels) && data.hotels.length > 0) {
        setHotels(data.hotels);
      }
    }
  }, [location.state]);

  const handleAddHotel = () => {
    if (hotels.length < 3) {
      setHotels([...hotels, { url: "", price: "" }]);
    }
  };

  const handleRemoveHotel = (index) => {
    const newHotels = hotels.filter((_, i) => i !== index);
    setHotels(newHotels);
  };

  const handleHotelChange = (index, field, value) => {
    const newHotels = [...hotels];
    newHotels[index][field] = value;
    setHotels(newHotels);
  };

  const handleOfferChange = (field, value) => {
    setOfferDetails(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async () => {
    setMessage(null);
    setLoading(true);

    const validHotels = hotels.filter(h => h.url && h.price);
    if (validHotels.length === 0) {
      setMessage({ type: "error", text: "Please add at least one hotel with a URL and Price." });
      setLoading(false);
      return;
    }
    
    if (!offerDetails.customerName || !offerDetails.customerEmail) {
      setMessage({ type: "error", text: "Customer Name and Email are required." });
      setLoading(false);
      return;
    }

    try {
      setMessage({ type: "info", text: "Saving offer details..." });
      
      const offerPayload = {
        ...offerDetails,
        dates: `From ${offerDetails.dateFrom} To ${offerDetails.dateTo}`,
        hotels: validHotels, // Send hotels to be saved
	agentNumber: offerDetails.agentNumber
      };
      
      await axios.post("/api/offers", offerPayload);
      
      setMessage({ type: "info", text: "Generating PDF..." });
      
      const formatDateRange = (start, end) => {
        if (!start || !end) return "";
        const s = new Date(start + "T00:00:00");
        const e = new Date(end + "T00:00:00");
        
        // If same month and year
        if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
          return `${s.toLocaleDateString('en-US', { month: 'long' })} ${s.getDate()}-${e.getDate()}, ${s.getFullYear()}`;
        }
        // If same year, different month
        if (s.getFullYear() === e.getFullYear()) {
          return `${s.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${s.getFullYear()}`;
        }
        // Different years
        return `${s.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} - ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      };

      const formattedDates = formatDateRange(offerDetails.dateFrom, offerDetails.dateTo);

      const payload = {
        hotels: validHotels.map(h => ({ ...h, price: Number(h.price) })),
        email: offerDetails.customerEmail,
        customerName: offerDetails.customerName,
        agentName: offerDetails.agentName,
        cityState: offerDetails.cityState,
        dates: formattedDates, // Use formatted string here
	agentNumber: offerDetails.agentNumber
      };

      const response = await axios.post("/api/scrape", payload, {
        responseType: "json",
	timeout: 300000
      });

      if (response.data.status === "success") {
        setMessage({ type: "success", text: `Offer saved & PDF sent to ${offerDetails.customerEmail}!` });
      } else {
        setMessage({ type: "error", text: "Offer saved, but failed to send email." });
      }

    } catch (error) {
      console.error(error);
      let errMsg = "An error occurred.";
      if (error.response && error.response.data) {
        errMsg = error.response.data.message || errMsg;
      }
      setMessage({ type: "error", text: errMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("isAuthenticated");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center p-4">
      <div className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-2xl w-full max-w-4xl border border-white/20 my-8">
        
        <div className="text-center mb-8 relative">
          <div className="absolute right-0 top-0 flex flex-col gap-2 items-end">
            <button 
              onClick={() => navigate("/offers")}
              className="text-xs md:text-sm bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold py-2 px-4 rounded-lg transition whitespace-nowrap"
            >
               View All Offers →
            </button>
            <button 
              onClick={handleLogout}
              className="text-xs md:text-sm bg-red-50 hover:bg-red-100 text-red-600 font-semibold py-2 px-4 rounded-lg transition whitespace-nowrap"
            >
               Logout
            </button>
          </div>
          <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-700 to-pink-700">
            Hotel Offer Generator
          </h1>
          <p className="text-gray-600 mt-2">
            Save offer details and generate comparison PDF.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* LEFT COLUMN: Offer Details */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-800 border-b pb-2">Offer Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Customer Name *</label>
                <input
                  type="text"
                  value={offerDetails.customerName}
                  onChange={(e) => handleOfferChange("customerName", e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Customer Email *</label>
                <input
                  type="email"
                  value={offerDetails.customerEmail}
                  onChange={(e) => handleOfferChange("customerEmail", e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Agent Name</label>
                <input
                  type="text"
                  value={offerDetails.agentName}
                  onChange={(e) => handleOfferChange("agentName", e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Agent Number</label>
                <input
                  type="text"
                  value={offerDetails.agentNumber}
                  onChange={(e) => handleOfferChange("agentNumber", e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Show Name</label>
                <input
                  type="text"
                  value={offerDetails.showName}
                  onChange={(e) => handleOfferChange("showName", e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Company Name</label>
                <input
                  type="text"
                  value={offerDetails.companyName}
                  onChange={(e) => handleOfferChange("companyName", e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">City / State</label>
                <input
                  type="text"
                  value={offerDetails.cityState}
                  onChange={(e) => handleOfferChange("cityState", e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
              <div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Check-in</label>
                    <input
                      type="date"
                      value={offerDetails.dateFrom || ""}
                      onChange={(e) => handleOfferChange("dateFrom", e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Check-out</label>
                    <input
                      type="date"
                      value={offerDetails.dateTo || ""}
                      onChange={(e) => handleOfferChange("dateTo", e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Hotels */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-800 border-b pb-2">Hotels (Max 3)</h2>

            {hotels.map((hotel, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200 animate-fadeIn relative">
                <div className="mb-2">
                  <input
                    type="text"
                    placeholder="Results in Booking.com Hotel URL"
                    value={hotel.url}
                    onChange={(e) => handleHotelChange(index, "url", e.target.value)}
                    className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                  />
                </div>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Price (€)"
                    value={hotel.price}
                    onChange={(e) => handleHotelChange(index, "price", e.target.value)}
                    className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                  />
                </div>
                {hotels.length > 1 && (
                  <button
                    onClick={() => handleRemoveHotel(index)}
                    className="absolute top-2 right-2 text-red-400 hover:text-red-600"
                    title="Remove"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={handleAddHotel}
              disabled={hotels.length >= 3}
              className={`w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 hover:border-indigo-300 hover:text-indigo-600 transition-all ${
                hotels.length >= 3 ? "opacity-50 cursor-not-allowed hidden" : ""
              }`}
            >
              + Add Another Hotel
            </button>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-100">
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-indigo-600 to-pink-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              "Save Offer & Generate PDF"
            )}
          </button>

          {message && (
            <div className={`mt-4 p-3 rounded-lg text-center font-medium animate-fadeIn ${
              message.type === "success" ? "bg-green-100 text-green-800" : 
              message.type === "error" ? "bg-red-100 text-red-800" : "bg-blue-100 text-blue-800"
            }`}>
              {message.text}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default Dashboard;
